// ChatbotDashboard.jsx
// Cambia USE_MOCK = false y API_BASE para conectar tu backend

import { useState, useEffect, useCallback, useRef } from "react";
import { ReloadOutlined, BulbOutlined } from "@ant-design/icons";
import { getConversations, getMessages, getMetrics, sendMessage, updateState, reprocessMessage as reprocessCRMMessage, markConversationRead, markConversationUnread, sendFeedback, forceFeedbackImprovement } from "../../apis/crmApi";
import DiffViewer from '../../components/DiffViewer';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

// ── Config de estados ───────────────────────────────────────────────────────
const STATE_CFG = {
  active:         { label: "Activa",          badge: "primary",   icon: "🤖" },
  human_required: { label: "Requiere humano", badge: "danger",    icon: "🙋" },
  booked:         { label: "Agendada",        badge: "success",   icon: "✅" },
  completed:      { label: "Completada",      badge: "secondary", icon: "✔️" },
  abandoned:      { label: "Abandonada",      badge: "warning",   icon: "⚠️" },
};
const BOOKING_CFG = {
  pending:   { label: "Pendiente",  badge: "warning" },
  confirmed: { label: "Confirmada", badge: "success" },
  cancelled: { label: "Cancelada",  badge: "danger"  },
};
const BADGE_COLORS = { primary: "#007bff", danger: "#dc3545", success: "#28a745", secondary: "#6c757d", warning: "#ffc107" };

const timeAgo = (d) => d ? dayjs.utc(d).fromNow() : "—";
const fmtTime = (d) => {
  if (!d) return "—";
  const date = dayjs.utc(d).tz(dayjs.tz.guess());
  const isToday = date.isSame(dayjs(), 'day');
  return isToday ? date.format('HH:mm') : date.format('DD/MM/YYYY HH:mm');
};
const fmtDT = (d) => d ? dayjs(d).format("DD/MM HH:mm") : "—";
const joinArr = (v) => Array.isArray(v) ? v.join(", ") : (v || "");

export default function CRM() {
  const [convs, setConvs] = useState([]);
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("convs");
  const [text, setText] = useState("");
  const bottomRef = useRef(null);
  const chatBodyRef = useRef(null);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= 768 : false);
  const [mobileView, setMobileView] = useState("list"); // 'list' or 'chat' for mobile
  
  // Feedback modal state
  const [feedbackModal, setFeedbackModal] = useState(null); // { userMsg, botMsg, botMsgId }
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackResult, setFeedbackResult] = useState(null); // { type, detail }
  const [forcing, setForcing] = useState(false);
  const [improvedIds, setImprovedIds] = useState([]);

  const loadConvs = useCallback(async () => {
    try {
      let response = await getConversations(filters);
      setConvs(response.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [filters]);

  const scrollToBottom = (smooth = true) => {
    bottomRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "instant" });
  };

  const loadMsgs = useCallback(async (id) => {
    try {
      let messages = await getMessages(id)
      const raw = messages.data || [];
      // Mostrar todos los mensajes (sin deduplicación); la BD ya está limpia
      setMsgs(raw);
      // Detectar mensajes ya marcados como mejorados por el backend (desde `message_improvements`)
      try {
        const improved = (raw || []).filter(m => m.improvement && m.improvement.method).map(m => m.id);
        setImprovedIds(improved);
      } catch (e) {
        // ignore
      }
    } catch (e) { console.error(e); }
  }, []);

  const loadMetrics = useCallback(async () => {
    try {
      let response = await getMetrics();
      setMetrics(response.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    loadConvs();
    loadMetrics();
  }, [loadConvs, loadMetrics]);


  const selectConv = async (c) => {
    setSel(c);
    // Mark conversation as read on the backend
    if (c.is_unread) {
      try {
        await markConversationRead(c.id);
        // Update local state is_unread for instant UI feedback
        setConvs(prev => prev.map(conv => conv.id === c.id ? { ...conv, is_unread: false } : conv));
      } catch (e) { console.error("Error marking as read", e); }
    }
    await loadMsgs(c.id);
    // Always jump to bottom when switching conversations
    setTimeout(() => scrollToBottom(false), 50);
    if (isMobile) setMobileView("chat");
  };

  const markUnread = async () => {
    if (!sel) return;
    try {
      await markConversationUnread(sel.id);
      // Update local state is_unread for instant UI feedback
      setConvs(prev => prev.map(conv => conv.id === sel.id ? { ...conv, is_unread: true } : conv));
    } catch (e) { console.error("Error marking as unread", e); }
  };


  useEffect(() => {
    const onResize = () => {
      const m = window.innerWidth <= 768;
      setIsMobile(m);
      if (!m) setMobileView("list");
    };
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => { if (isMobile && sel) setMobileView("chat"); }, [isMobile, sel]);

  const handleSend = async () => {
    if (!sel || !text.trim() || sending) return;
    setSending(true); 
    const formatedText = text.trim(); setText("");
    try {
      await sendMessage(sel.id, formatedText); 
      await loadMsgs(sel.id);
    } catch (e) { 
      console.error(e); 
      alert("Error al enviar mensaje: " + (e.response?.data?.error || e.message));
    } finally { setSending(false); }
  };

  const handleState = async (newState) => {
    if (!sel) return;
    const id = sel.id;
    try {
      await updateState(id, newState);
      await loadConvs();
      // actualizar la conversación seleccionada localmente para que la UI refleje el nuevo estado
      setSel((prev) => prev ? { ...prev, state: newState } : prev);
      // recargar mensajes por si la API los modificó al cambiar el estado
      await loadMsgs(id);
    } catch (e) { console.error(e); }
  };

  const openFeedbackModal = (botMsg, msgIndex) => {
    // Find the preceding user message
    let userMsg = "";
    for (let i = msgIndex - 1; i >= 0; i--) {
      if (msgs[i].sender === "user") {
        userMsg = msgs[i].text;
        break;
      }
    }
    setFeedbackModal({ 
      userMsg, 
      botMsg: botMsg.text, 
      botMsgId: botMsg.id, 
      agentName: botMsg.agent_name
    });
    setFeedbackText("");
    setFeedbackResult(null);
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackText.trim() || !feedbackModal || feedbackSending) return;
    setFeedbackSending(true);
    try {
      const res = await sendFeedback({
        user_message: feedbackModal.userMsg,
        bot_response: feedbackModal.botMsg,
        human_feedback: feedbackText.trim(),
        conversation_id: sel?.id,
        bot_message_id: feedbackModal.botMsgId,
        agent_name: feedbackModal.agentName
      });
      setFeedbackResult(res.data);
    } catch (e) {
      console.error("Feedback error:", e);
      setFeedbackResult({ success: false, error: "Error al enviar feedback" });
    } finally {
      setFeedbackSending(false);
    }
  };

  const handleForceImprovement = async () => {
    if (!feedbackResult?.candidate || !feedbackModal || forcing) return;
    setForcing(true);
    try {
      const payload = {
        agent_name: feedbackModal.agentName,
        conversation_id: sel?.id,
        user_message: feedbackModal.userMsg,
        bot_response: feedbackModal.botMsg,
        human_feedback: feedbackText.trim(),
        bot_message_id: feedbackModal.botMsgId,
        candidate_prompt: feedbackResult.candidate.prompt,
        candidate_reply: feedbackResult.candidate.reply,
        candidate_reward: feedbackResult.candidate.reward
      };
      const res = await forceFeedbackImprovement(payload);
      setFeedbackResult(res.data);
      await loadConvs();
      if (sel?.id) await loadMsgs(sel.id);
    } catch (e) {
      console.error("Force improvement error:", e);
      setFeedbackResult({ success: false, error: "Error al forzar mejora" });
    } finally {
      setForcing(false);
    }
  };

  const handleReprocess = async (messageId) => {
    if (!messageId || sending) return;
    setSending(true);
    try {
      const response = await reprocessCRMMessage(messageId);
      if (response.data && response.data.reply) {
        setText(response.data.reply);
        // Actualizar estado local para permitir respuesta inmediata y recargar lista
        setSel(prev => prev ? { ...prev, state: "human_required" } : prev);
        loadConvs();
        // Desplazar al final para ver el input con el texto
        setTimeout(() => scrollToBottom(true), 100);
      }
    } catch (e) {
      console.error(e);
      alert("Error al generar sugerencia");
    } finally {
      setSending(false);
    }
  };

  const urgent = convs.filter((c) => c.state === "human_required").length;
  const canRespond = sel?.state === "human_required";

  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/4.6.2/css/bootstrap.min.css" />
      <div style={{ fontFamily: "Inter,system-ui,sans-serif", height: "88dvh", display: "flex", flexDirection: "column" }}>

        {/* Navbar */}
        <nav style={{ background: "#1a1a2e", height: 52, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", flexShrink: 0 }}>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>💬 Chatbot Dashboard</span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {urgent > 0 && <span className="badge badge-danger" style={{ fontSize: 12, padding: "5px 12px", borderRadius: 20 }}>🙋 {urgent} requieren atención</span>}
            <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: 8, padding: 3, display: "flex", gap: 3 }}>
              {[["convs", "💬 Conversaciones"], ["metrics", "📊 Métricas"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} style={{ border: "none", borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer", background: tab === k ? "#fff" : "transparent", color: tab === k ? "#1a1a2e" : "#fff" }}>{l}</button>
              ))}
            </div>
          </div>
        </nav>

        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {tab === "metrics" ? (
            <div style={{ overflowY: "auto", padding: 24 }}>
              <h5 className="font-weight-bold mb-4">📊 Panel de Métricas</h5>
              {!metrics ? <div className="text-center py-5"><div className="spinner-border text-primary" /></div> : (<>
                <div className="row mb-4">
                  {[["Total", metrics.total, "#6c757d", "💬"], ["Activas", metrics.active, "#007bff", "🤖"], ["Req. humano", metrics.human_required, "#dc3545", "🙋"], ["Agendadas", metrics.booked, "#28a745", "✅"], ["Conversión", `${metrics.conversion_rate}%`, "#17a2b8", "📈"], ["Resp. prom.", `${metrics.avg_response_time_seconds}s`, "#fd7e14", "⚡"]].map(([label, value, color, icon]) => (
                    <div className="col-6 col-md-4 col-xl-2 mb-3" key={label}>
                      <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${color}`, borderRadius: 10 }}>
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <div><div className="text-muted" style={{ fontSize: 11 }}>{label}</div><div className="font-weight-bold mt-1" style={{ fontSize: 24, color }}>{value}</div></div>
                            <span style={{ fontSize: 28 }}>{icon}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white font-weight-bold">Conversaciones por estado</div>
                  <div className="card-body">
                    {Object.entries(STATE_CFG).map(([k, c]) => { const count = metrics[k] ?? 0; const pct = metrics.total ? Math.round((count / metrics.total) * 100) : 0; return (
                      <div key={k} className="mb-3">
                        <div className="d-flex justify-content-between mb-1" style={{ fontSize: 13 }}><span>{c.icon} {c.label}</span><span className="font-weight-bold">{count} <span className="text-muted font-weight-normal">({pct}%)</span></span></div>
                        <div className="progress" style={{ height: 8, borderRadius: 4 }}><div className="progress-bar" style={{ width: `${pct}%`, background: BADGE_COLORS[c.badge], borderRadius: 4 }} /></div>
                      </div>
                    ); })}
                  </div>
                </div>

                {(() => {
                  const testSummaries = metrics.ab_test_outcomes?.tests?.length > 0
                    ? metrics.ab_test_outcomes.tests
                    : (metrics.ab_test_outcomes?.variants?.length > 0 ? [{ test_name: metrics.ab_test_outcomes.test_name || "A/B test", variants: metrics.ab_test_outcomes.variants }] : []);

                  return testSummaries.length > 0 ? (
                    <div className="card border-0 shadow-sm mt-4">
                      <div className="card-header bg-white font-weight-bold">Resultados A/B por test</div>
                      <div className="card-body">
                        {testSummaries.map((testSummary) => (
                          <div key={testSummary.test_name} className="mb-4">
                            <div className="font-weight-bold mb-2">{testSummary.test_name}</div>
                            {(testSummary.variants || []).map((variant) => (
                              <div key={`${testSummary.test_name}-${variant.variant}`} className="mb-3">
                                <div className="d-flex justify-content-between mb-1" style={{ fontSize: 13 }}>
                                  <span className="font-weight-bold">Variante {variant.variant}</span>
                                  <span className="text-muted">Score medio: {variant.average_outcome_score}</span>
                                </div>
                                <div className="small text-muted">
                                  Planes: {variant.outcomes.plan_purchase} · Cortesías: {variant.outcomes.courtesy_booking} · Seguimiento: {variant.outcomes.continued_conversation} · Negativo: {variant.outcomes.negative}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}
              </>)}
            </div>
          ) : (
            <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
              {!isMobile ? (
                <>
                  {/* Sidebar */}
                  <div style={{ width: 290, flexShrink: 0, borderRight: "1px solid #dee2e6", background: "#fff", display: "flex", flexDirection: "column" }}>
                    <div style={{ padding: "12px 12px 8px", borderBottom: "1px solid #dee2e6" }}>
                      <div className="font-weight-bold mb-2" style={{ fontSize: 14 }}>Conversaciones</div>
                      <input className="form-control form-control-sm mb-2" placeholder="🔍 Buscar usuario..." value={filters.search || ""} onChange={(e) => setFilters({ ...filters, search: e.target.value || undefined })} />
                      <select className="form-control form-control-sm" value={filters.state || ""} onChange={(e) => setFilters({ ...filters, state: e.target.value || undefined })}>
                        <option value="">Todos los estados</option>
                        {Object.entries(STATE_CFG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                      </select>
                    </div>
                    <div style={{ overflowY: "auto", flex: 1 }}>
                      {loading ? <div className="text-center py-5 text-muted"><div className="spinner-border spinner-border-sm mr-2" />Cargando...</div>
                        : convs.length === 0 ? <div className="text-center py-5 text-muted"><div style={{ fontSize: 32 }}>💬</div><p className="mt-2 small">Sin conversaciones</p></div>
                        : convs.map((c) => { const cf = STATE_CFG[c.state] || {}; const isSel = c.id === sel?.id; const isU = c.state === "human_required"; const hasUnread = c.is_unread; return (
                          <div key={c.id} onClick={() => selectConv(c)} style={{ padding: "10px 12px", borderBottom: "1px solid #f0f0f0", cursor: "pointer", background: isSel ? "#e8f4fd" : isU ? "#fff8f8" : "#fff", borderLeft: `4px solid ${isU ? "#dc3545" : isSel ? "#007bff" : "transparent"}` }}>
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="font-weight-bold text-truncate" style={{ fontSize: 13, maxWidth: 140 }}>{c.user?.name || c.user?.phone}</span>
                              <div className="d-flex align-items-center" style={{ gap: 4, flexShrink: 0 }}>
                                {hasUnread && <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#dc3545", display: "inline-block", flexShrink: 0 }} title="Nuevo mensaje" />}
                                <small className="text-muted" style={{ fontSize: 11 }}>{timeAgo(c.last_user_message_at)}</small>
                              </div>
                            </div>
                            <div className="d-flex flex-wrap mt-1" style={{ gap: 3 }}>
                              <span className={`badge badge-${cf.badge}`} style={{ fontSize: 10 }}>{cf.icon} {cf.label}</span>
                              {hasUnread && <span className="badge badge-danger" style={{ fontSize: 10 }}>🔔 Nuevo</span>}
                              {c.booking_status && <span className={`badge badge-${BOOKING_CFG[c.booking_status]?.badge}`} style={{ fontSize: 10 }}>{BOOKING_CFG[c.booking_status]?.label}</span>}
                            </div>
                            {c.goal && <div className="text-muted text-truncate mt-1" style={{ fontSize: 11 }}>🎯 {c.goal}</div>}
                          </div>
                        ); })}
                    </div>
                  </div>

                  {/* Chat */}
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    {!sel ? (
                      <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted"><div style={{ fontSize: 52 }}>💬</div><p className="mt-3">Selecciona una conversación</p></div>
                    ) : (<>
                      {/* Header */}
                      <div style={{ padding: "12px 20px", borderBottom: "1px solid #dee2e6", background: "#fff", flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <div className="font-weight-bold" style={{ fontSize: 15 }}>{sel.user?.name}</div>
                          <div className="text-muted small">{sel.user?.phone}</div>
                          <div className="d-flex flex-wrap mt-1" style={{ gap: 4 }}>
                            <span className={`badge badge-${STATE_CFG[sel.state]?.badge}`}>{STATE_CFG[sel.state]?.icon} {STATE_CFG[sel.state]?.label}</span>
                            {sel.booking_status && <span className={`badge badge-${BOOKING_CFG[sel.booking_status]?.badge}`}>{BOOKING_CFG[sel.booking_status]?.label}</span>}
                            {sel.confirmed_attendance && <span className="badge badge-info">Asistencia confirmada</span>}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canRespond && <button className="btn btn-sm btn-success" onClick={() => handleState("active")}>✅ Resolver</button>}
                          {sel.state === "active" && <button className="btn btn-sm btn-outline-danger" onClick={() => handleState("human_required")}>🙋 Escalar</button>}
                          <button className="btn btn-sm btn-outline-secondary" onClick={markUnread} title="Marcar como no leída" style={{ fontSize: 11 }}>🔴 No leída</button>
                        </div>
                      </div>

                      {/* Context bar */}
                      <div style={{ padding: "8px 20px", borderBottom: "1px solid #dee2e6", background: "#f8f9fa", fontSize: 12, display: "flex", flexWrap: "wrap", gap: 14, flexShrink: 0 }}>
                        {sel.goal && <span>🎯 <strong>Objetivo:</strong> {sel.goal}</span>}
                        {sel.suggested_class && <span>🏋️ <strong>Clase:</strong> {sel.suggested_class}</span>}
                        {sel.suggested_datetime && <span>📅 <strong>Fecha:</strong> {fmtDT(sel.suggested_datetime)}</span>}
                        {sel.physical_limits?.length > 0 && <span>⚠️ <strong>Límites físicos:</strong> {joinArr(sel.physical_limits)}</span>}
                        {sel.preferences?.length > 0 && <span>💚 <strong>Preferencias:</strong> {joinArr(sel.preferences)}</span>}
                      </div>

                      {/* Messages */}
                      <div ref={chatBodyRef} style={{ flex: 1, overflowY: "auto", padding: "16px 20px", background: "#f0f2f5" }}>
                        {msgs.length === 0 ? <div className="text-center text-muted py-5 small">Sin mensajes</div>
                          : msgs.map((m) => {
                            if (m.sender === "system") return <div key={m.id} className="text-center my-3"><span className="badge badge-secondary px-3 py-1" style={{ borderRadius: 20, fontSize: 11 }}>{m.text}</span></div>;
                            const isUser = m.sender === "user"; const isBot = m.sender === "bot";
                            return (
                              <div key={m.id} className={`d-flex mb-2 align-items-end ${isUser ? "" : "flex-row-reverse"}`}>
                                <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 28, height: 28, fontSize: 12, background: isUser ? "#e9ecef" : isBot ? "#007bff" : "#28a745", [isUser ? "marginRight" : "marginLeft"]: 8 }}>
                                  {isUser ? "👤" : isBot ? "🤖" : "👨‍💼"}
                                </div>
                                <div style={{ maxWidth: "68%" }}>
                                  <div style={{ borderRadius: isUser ? "4px 12px 12px 12px" : "12px 4px 12px 12px", background: isUser ? "#fff" : isBot ? "#007bff" : "#28a745", color: isUser ? "#333" : "#fff", fontSize: 13, padding: "8px 14px", lineHeight: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.08)", whiteSpace: "pre-line" }}>
                                    {m.text}
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center mt-1" style={{ padding: "0 4px" }}>
                                    <div className="text-muted" style={{ fontSize: 10 }}>{!isUser && (isBot ? "Bot · " : "Agente · ")}{fmtTime(m.created_at)}</div>
                                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                      {isUser && (
                                        <button 
                                          onClick={() => handleReprocess(m.id)} 
                                          style={{ border: "none", background: "transparent", cursor: "pointer", padding: "0", fontSize: 12, opacity: 0.4, display: "flex", alignItems: "center" }}
                                          title="Reprocesar"
                                          disabled={sending}
                                        >
                                          <ReloadOutlined spin={sending} style={{ color: "#666" }} />
                                        </button>
                                      )}
                                      {isBot && (
                                        <>
                                          {improvedIds.includes(m.id) && <span className="badge badge-success" style={{ fontSize: 10, marginRight: 6 }}>Mejora aplicada</span>}
                                          <button
                                            onClick={() => openFeedbackModal(m, msgs.indexOf(m))}
                                            style={{ border: "none", background: "transparent", cursor: "pointer", padding: "0", fontSize: 12, opacity: 0.4, display: "flex", alignItems: "center" }}
                                            title="Dar feedback"
                                          >
                                            <BulbOutlined style={{ color: "#f59e0b" }} />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        }
                        <div ref={bottomRef} />
                      </div>

                      {/* Input */}
                      {(canRespond || text.length > 0) ? (
                        <div style={{ padding: "12px 20px", borderTop: "1px solid #dee2e6", background: "#fff", flexShrink: 0 }}>
                          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                            <textarea className="form-control" rows={2} placeholder="Escribe tu mensaje... (Enter para enviar, Shift+Enter nueva línea)" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }} disabled={sending} style={{ resize: "none", borderRadius: 12, fontSize: 13 }} />
                            <button className="btn btn-primary" onClick={handleSend} disabled={!text.trim() || sending} style={{ borderRadius: 12, padding: "10px 18px", flexShrink: 0 }}>
                              {sending ? <span className="spinner-border spinner-border-sm" /> : "➤"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ padding: "12px 20px", borderTop: "1px solid #dee2e6", background: "#fff", textAlign: "center", color: "#888", fontSize: 13, flexShrink: 0 }}>
                          {sel.state === "active" && "🤖 El bot está manejando esta conversación"}
                          {sel.state === "booked" && "✅ Agendada — solo lectura"}
                          {sel.state === "completed" && "✔️ Completada — solo lectura"}
                          {sel.state === "abandoned" && "⚠️ Abandonada — solo lectura"}
                        </div>
                      )}
                    </>) }
                  </div>
                </>
              ) : (
                <>
                  {mobileView === "list" ? (
                    <div style={{ width: "100%", display: "flex", flexDirection: "column", background: "#fff" }}>
                      <div style={{ padding: "12px 12px 8px", borderBottom: "1px solid #dee2e6" }}>
                        <div className="font-weight-bold mb-2" style={{ fontSize: 14 }}>Conversaciones</div>
                        <input className="form-control form-control-sm mb-2" placeholder="🔍 Buscar usuario..." value={filters.search || ""} onChange={(e) => setFilters({ ...filters, search: e.target.value || undefined })} />
                        <select className="form-control form-control-sm" value={filters.state || ""} onChange={(e) => setFilters({ ...filters, state: e.target.value || undefined })}>
                          <option value="">Todos los estados</option>
                          {Object.entries(STATE_CFG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                        </select>
                      </div>
                      <div style={{ overflowY: "auto", flex: 1 }}>
                        {loading ? <div className="text-center py-5 text-muted"><div className="spinner-border spinner-border-sm mr-2" />Cargando...</div>
                          : convs.length === 0 ? <div className="text-center py-5 text-muted"><div style={{ fontSize: 32 }}>💬</div><p className="mt-2 small">Sin conversaciones</p></div>
                          : convs.map((c) => { const cf = STATE_CFG[c.state] || {}; const isSel = c.id === sel?.id; const isU = c.state === "human_required"; const hasUnread = c.is_unread; return (
                            <div key={c.id} onClick={() => selectConv(c)} style={{ padding: "10px 12px", borderBottom: "1px solid #f0f0f0", cursor: "pointer", background: isSel ? "#e8f4fd" : isU ? "#fff8f8" : "#fff", borderLeft: `4px solid ${isU ? "#dc3545" : isSel ? "#007bff" : "transparent"}` }}>
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="font-weight-bold text-truncate" style={{ fontSize: 13, maxWidth: 200 }}>{c.user?.name || c.user?.phone}</span>
                                <div className="d-flex align-items-center" style={{ gap: 4, flexShrink: 0 }}>
                                  {hasUnread && <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#dc3545", display: "inline-block" }} title="Nuevo mensaje" />}
                                  <small className="text-muted" style={{ fontSize: 11 }}>{timeAgo(c.last_user_message_at)}</small>
                                </div>
                              </div>
                              <div className="d-flex flex-wrap mt-1" style={{ gap: 3 }}>
                                <span className={`badge badge-${cf.badge}`} style={{ fontSize: 10 }}>{cf.icon} {cf.label}</span>
                                {hasUnread && <span className="badge badge-danger" style={{ fontSize: 10 }}>🔔 Nuevo</span>}
                                {c.booking_status && <span className={`badge badge-${BOOKING_CFG[c.booking_status]?.badge}`} style={{ fontSize: 10 }}>{BOOKING_CFG[c.booking_status]?.label}</span>}
                              </div>
                              {c.goal && <div className="text-muted text-truncate mt-1" style={{ fontSize: 11 }}>🎯 {c.goal}</div>}
                            </div>
                          ); })}
                      </div>
                    </div>
                  ) : (
                    <div style={{ width: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                      {!sel ? (
                        <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted"><div style={{ fontSize: 52 }}>💬</div><p className="mt-3">Selecciona una conversación</p></div>
                      ) : (<>
                        {/* Mobile header with back button */}
                        <div style={{ padding: "12px 12px", borderBottom: "1px solid #dee2e6", background: "#fff", flexShrink: 0, display: "flex", alignItems: "center", gap: 12 }}>
                          <button className="btn btn-sm btn-light" onClick={() => setMobileView("list")}>←</button>
                          <div style={{ flex: 1 }}>
                            <div className="font-weight-bold" style={{ fontSize: 15 }}>{sel.user?.name}</div>
                            <div className="text-muted small">{sel.user?.phone}</div>
                          </div>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {canRespond && <button className="btn btn-sm btn-success" onClick={() => handleState("active")}>✅ Resolver</button>}
                            {sel.state === "active" && <button className="btn btn-sm btn-outline-danger" onClick={() => handleState("human_required")}>🙋 Escalar</button>}
                            <button className="btn btn-sm btn-outline-secondary" onClick={markUnread} title="Marcar como no leída" style={{ fontSize: 11 }}>🔴</button>
                          </div>
                        </div>

                        {/* Context bar */}
                        <div style={{ padding: "8px 12px", borderBottom: "1px solid #dee2e6", background: "#f8f9fa", fontSize: 12, display: "flex", flexWrap: "wrap", gap: 14, flexShrink: 0 }}>
                          {sel.goal && <span>🎯 <strong>Objetivo:</strong> {sel.goal}</span>}
                          {sel.suggested_class && <span>🏋️ <strong>Clase:</strong> {sel.suggested_class}</span>}
                          {sel.suggested_datetime && <span>📅 <strong>Fecha:</strong> {fmtDT(sel.suggested_datetime)}</span>}
                        </div>

                        {/* Messages */}
                        <div style={{ flex: 1, overflowY: "auto", padding: "16px 12px", background: "#f0f2f5" }}>
                          {msgs.length === 0 ? <div className="text-center text-muted py-5 small">Sin mensajes</div>
                            : msgs.map((m) => {
                              if (m.sender === "system") return <div key={m.id} className="text-center my-3"><span className="badge badge-secondary px-3 py-1" style={{ borderRadius: 20, fontSize: 11 }}>{m.text}</span></div>;
                              const isUser = m.sender === "user"; const isBot = m.sender === "bot";
                              return (
                                <div key={m.id} className={`d-flex mb-2 align-items-end ${isUser ? "" : "flex-row-reverse"}`}>
                                  <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 28, height: 28, fontSize: 12, background: isUser ? "#e9ecef" : isBot ? "#007bff" : "#28a745", [isUser ? "marginRight" : "marginLeft"]: 8 }}>
                                    {isUser ? "👤" : isBot ? "🤖" : "👨‍💼"}
                                  </div>
                                  <div style={{ maxWidth: "76%" }}>
                                    <div style={{ borderRadius: isUser ? "4px 12px 12px 12px" : "12px 4px 12px 12px", background: isUser ? "#fff" : isBot ? "#007bff" : "#28a745", color: isUser ? "#333" : "#fff", fontSize: 13, padding: "8px 14px", lineHeight: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>
                                      {m.text}
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mt-1">
                                      <div className="text-muted" style={{ fontSize: 10 }}>{!isUser && (isBot ? "Bot · " : "Agente · ")}{fmtTime(m.created_at)}</div>
                                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                        {isBot && (
                                          <>
                                                {improvedIds.includes(m.id) && <span className="badge badge-success" style={{ fontSize: 10, marginRight: 6 }}>Mejora aplicada</span>}
                                                <button
                                                  onClick={() => openFeedbackModal(m, msgs.indexOf(m))}
                                                  style={{ border: "none", background: "transparent", cursor: "pointer", padding: "0", fontSize: 12, opacity: 0.5 }}
                                                  title="Dar feedback"
                                                >
                                                  <BulbOutlined style={{ color: "#f59e0b" }} />
                                                </button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    {isUser && (
                                      <button
                                        onClick={() => handleReprocess(m.id)}
                                        style={{ border: "none", background: "transparent", cursor: "pointer", padding: "0 4px", fontSize: 12, opacity: 0.5, display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}
                                        title="Reprocesar"
                                        disabled={sending}
                                      >
                                        <ReloadOutlined spin={sending} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          }
                          <div ref={bottomRef} />
                        </div>

                        {/* Input */}
                        {(canRespond || text.length > 0) ? (
                          <div style={{ padding: "12px", borderTop: "1px solid #dee2e6", background: "#fff", flexShrink: 0 }}>
                            <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                              <textarea className="form-control" rows={2} placeholder="Escribe tu mensaje... (Enter para enviar, Shift+Enter nueva línea)" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }} disabled={sending} style={{ resize: "none", borderRadius: 12, fontSize: 13 }} />
                              <button className="btn btn-primary" onClick={handleSend} disabled={!text.trim() || sending} style={{ borderRadius: 12, padding: "10px 18px", flexShrink: 0 }}>
                                {sending ? <span className="spinner-border spinner-border-sm" /> : "➤"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ padding: "12px", borderTop: "1px solid #dee2e6", background: "#fff", textAlign: "center", color: "#888", fontSize: 13, flexShrink: 0 }}>
                            {sel.state === "active" && "🤖 El bot está manejando esta conversación"}
                            {sel.state === "booked" && "✅ Agendada — solo lectura"}
                            {sel.state === "completed" && "✔️ Completada — solo lectura"}
                            {sel.state === "abandoned" && "⚠️ Abandonada — solo lectura"}
                          </div>
                        )}
                      </>) }
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Feedback Modal */}
        {feedbackModal && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setFeedbackModal(null)}>
            <div style={{ background: "#fff", borderRadius: 16, padding: 16, maxWidth: 900, width: "90%", maxHeight: '80vh', overflowY: 'auto', boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} onClick={e => e.stopPropagation()}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <h5 style={{ margin: 0, fontWeight: 700 }}>💡 Feedback sobre respuesta del bot</h5>
                <button aria-label="Cerrar" onClick={() => setFeedbackModal(null)} style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer", padding: 6 }} title="Cerrar">✕</button>
              </div>
              
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#666", textTransform: "uppercase" }}>Mensaje del usuario</label>
                <div style={{ background: "#f8f9fa", padding: "8px 12px", borderRadius: 8, fontSize: 13, marginTop: 4, color: "#333" }}>{feedbackModal.userMsg || "(sin mensaje previo)"}</div>
              </div>
              
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#666", textTransform: "uppercase" }}>Respuesta del bot</label>
                <div style={{ background: "#e8f4fd", padding: "8px 12px", borderRadius: 8, fontSize: 13, marginTop: 4, color: "#333" }}>{feedbackModal.botMsg}</div>
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#666", textTransform: "uppercase" }}>Tu corrección / feedback</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Ej: Aclara que el parqueadero es solo para bicicletas..."
                  value={feedbackText}
                  onChange={e => setFeedbackText(e.target.value)}
                  style={{ marginTop: 4, borderRadius: 8, fontSize: 13, resize: "none" }}
                  disabled={feedbackSending}
                />
              </div>
              
              {feedbackResult && (
                <div style={{ padding: "8px 12px", borderRadius: 8, marginBottom: 12, background: feedbackResult.success ? "#d4edda" : "#f8d7da", color: feedbackResult.success ? "#155724" : "#721c24", fontSize: 13 }}>
                  {feedbackResult.success ? (
                    <>
                      {feedbackResult.type === "knowledge" ? "📚" : "🧠"} {feedbackResult.detail}
                      {feedbackResult.candidate && (
                        <div style={{ marginTop: 10, padding: 10, background: "#fff3cd", borderRadius: 8 }}>
                          <DiffViewer original={feedbackResult.candidate.current_prompt} revised={feedbackResult.candidate.prompt} collapsedLines={6} />

                          <div style={{ fontSize: 12, fontWeight: 700, marginTop: 8 }}>Respuesta propuesta por el APO</div>
                          <div style={{ background: "#fff", padding: 8, borderRadius: 6, whiteSpace: "pre-line", color: "#333" }}>{feedbackResult.candidate.reply}</div>

                          <div className="text-muted small mt-1">Reward propuesto: {typeof feedbackResult.candidate.reward === 'number' ? feedbackResult.candidate.reward.toFixed(2) : '—'}</div>
                          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setFeedbackResult(null)}>Descartar</button>
                            <button className="btn btn-sm btn-success" onClick={handleForceImprovement} disabled={forcing}>{forcing ? <span className="spinner-border spinner-border-sm" /> : "Forzar mejora"}</button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>❌ {feedbackResult.error || "Error desconocido"}</>
                  )}
                </div>
              )}
              
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setFeedbackModal(null)} style={{ borderRadius: 8 }}>Cancelar</button>
                {!feedbackResult?.success && (
                  <button className="btn btn-sm btn-primary" onClick={handleFeedbackSubmit} disabled={!feedbackText.trim() || feedbackSending} style={{ borderRadius: 8, padding: "6px 18px" }}>
                    {feedbackSending ? <span className="spinner-border spinner-border-sm" /> : "Enviar feedback"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}